import axios from 'axios'
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { API } from "../helpers/constants";
import { handleAxiosErrors } from "../helpers/errorHandling";
import { EXHIBITION } from "../locales/components/namespaces";
import useUserData from "./user/useUserData";

const useFileFromApiForPreview = (options) => {
	const {
		fetchFileUrl = '',
		uploadFileUrl = '',
		uploadFileParams = null,
		deleteFileUrl = '',
		deleteFileParams = null
	} = options
	const { t } = useTranslation(EXHIBITION)
	const { token } = useUserData()
	const [loading, setLoading] = useState(false)
	const [loadingDelete, setLoadingDelete] = useState(false)
	const [file, setFile] = useState(null);
	
	const getFile = useCallback(() => {
		setLoading(true)
		axios({
			url: `${API}/en/${fetchFileUrl}`,
			method: 'get',
			responseType: "arraybuffer",
			headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
		}).then((response) => {
			const bytes = new Uint8Array(response.data);
			const contentType = response.headers['content-type'];
			
			// Detect PDF
			const isPDF = contentType === 'application/pdf' ||
				(bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46);
			
			// Detect JPEG
			const isJPEG = contentType?.startsWith('ima0ge/jpeg') ||
				(bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF);
			
			// Detect PNG
			const isPNG = contentType?.startsWith('image/png') ||
				(bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47);
			
			if (isPDF) {
				const blob = new Blob([response.data], { type: contentType });
				const url = URL.createObjectURL(blob);
				setFile({ type: 'pdf', url });
			} else if (isJPEG || isPNG) {
				const base64 = btoa(
					bytes.reduce((data, byte) => data + String.fromCharCode(byte), '')
				);
				setFile({ type: 'image', url: base64 });
			} else {
				console.warn('Unsupported file type');
			}
			setLoading(false)
		}).catch((error) => {
			setLoading(false)
		}).finally(() => {
			setLoading(false)
		})
	}, [fetchFileUrl, token])
	
	useEffect(() => {
		getFile()
	}, [getFile])
	
	const uploadFile = (file) => {
		setLoading(true)
		axios({
			url: `${API}/en/${uploadFileUrl}`,
			method: 'post',
			headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` },
			data: {
				file: file,
				...(Boolean(uploadFileParams) && {...uploadFileParams})
			}
		}).then((response) => {
			setLoading(true)
			getFile()
			enqueueSnackbar(t('File uploaded successfully!'), {
				variant: 'success',
			})
		}).catch((error) => {
			setLoading(false)
			const apiMessage = error?.response?.data?.message
			enqueueSnackbar(handleAxiosErrors(error, `${t('Failed to upload file!')} ${t(apiMessage)}`), {
				variant: 'error',
			})
		})
	}
	
	const deleteFile = (file) => {
		setLoadingDelete(true)
		axios({
			url: `${API}/en/${deleteFileUrl}`,
			method: 'post',
			headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` },
			data: {
			...(Boolean(deleteFileParams) && {...deleteFileParams})
			}
		}).then((response) => {
			setLoadingDelete(false)
			setFile('')
			enqueueSnackbar(t('File deleted successfully!'), {
				variant: 'success',
			})
		}).catch((error) => {
			setLoadingDelete(false)
			enqueueSnackbar(handleAxiosErrors(error, t('Failed to delete file!')), {
				variant: 'error',
				autoHideDuration: null
			})
		})
	}
	
	return {
		file,
		loading,
		loadingDelete,
		getFile,
		deleteFile,
		uploadFile,
	}
}

export default useFileFromApiForPreview